/**
 * Visitor time-on-site heartbeat.
 *
 * A single global hook installed at the App root that pings the
 * backend every 30 seconds — but only when the user is *actively
 * engaged* with the page. Activity = mouse, keyboard, scroll, touch,
 * or wheel events in the last 30s, plus tab-just-became-visible. A
 * tab left open in the background while the user is doing something
 * else won't accrue time.
 *
 * Combined with the visitor row's `firstSeenAt`, this gives the admin
 * a real cumulative active time-on-site for every browser, not the
 * misleading "tab was open for 2 hours" number a passive heartbeat
 * would produce.
 *
 * Visibility-aware: when the tab goes hidden we don't ping at all.
 * When it returns to visible we count that as activity (the user
 * came back) and re-baseline.
 *
 * Server-side cap: each ping is clamped to 60s by the backend, so
 * even if a runaway delta sneaks through, the data stays sane.
 */

import { useEffect } from 'react';
import api from './api';
import { getOrCreateVisitorId } from './visitorId';
import { capturePageLoadAudience, getAudience, getLandingVariant } from './audience';
import { takeTouchesForHeartbeat } from './attribution';

/**
 * The browser's own timezone and primary language — a coarse location proxy,
 * read locally with no network call, no IP, and no precise geolocation. This is
 * deliberately the low-fidelity signal: enough to see whether paid traffic is
 * even in the target market, not enough to identify anyone. Wrapped in try/catch
 * because `resolvedOptions()` can throw in exotic/locked-down runtimes, and a
 * heartbeat must never fail on account of a nice-to-have field.
 */
function readCoarseLocale(): { timezone?: string; language?: string } {
  let timezone: string | undefined;
  let language: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    /* leave undefined */
  }
  try {
    language = navigator.language || undefined;
  } catch {
    /* leave undefined */
  }
  return { timezone, language };
}

/**
 * Heartbeat cadence. 60 seconds still gives a good read-out of session
 * length while halving the network traffic this generates — the ping was
 * roughly a sixth of ALL production requests at 30s, and cutting its volume
 * (alongside making the backend lookup an indexed query rather than a table
 * scan) keeps the monitoring from competing with real page-load calls. The
 * backend clamps a single ping's delta to 90s, leaving headroom for jitter.
 */
const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Initial ping delay. The first ping fires this long after page
 * load so a visitor who actively engages in <30s still gets credit
 * for being on the site. Anything shorter than this is a fly-by tab
 * open and not worth tracking.
 */
const INITIAL_PING_DELAY_MS = 5_000;

/**
 * Idle threshold. If no input event has happened in this window, we
 * skip the ping entirely AND advance the baseline so the idle
 * period never gets counted as active time. Set equal to the
 * heartbeat interval so a single missed-activity tick = idle.
 */
const IDLE_THRESHOLD_MS = HEARTBEAT_INTERVAL_MS;

/**
 * DOM events that count as "the user is alive and engaged". Mousemove
 * is included so passive reading (where the cursor drifts) still
 * counts — but the listener is `passive: true` so it never blocks
 * scroll or other input handling. Scroll covers reading-by-scrolling.
 * Touch/keydown cover mobile and form usage.
 */
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

/**
 * Installs the heartbeat. Call once near the App root. No cleanup is
 * needed in production (the SPA never unmounts the App), but the
 * hook still returns a cleanup function for hot-reload safety.
 */
export function useVisitorHeartbeat() {
  useEffect(() => {
    const visitorId = getOrCreateVisitorId();
    if (!visitorId) return; // localStorage unavailable — silently skip.

    // Stamp the audience param into localStorage if this page load
    // had ?audience=… in the URL. Subsequent heartbeats and tracking
    // calls read from localStorage so the audience persists across
    // route changes within the same browser session.
    capturePageLoadAudience();

    /** Wall-clock time of the last successful (or counted) ping. */
    let lastPingAt = Date.now();
    /**
     * Wall-clock time of the most recent user input event. Starts at
     * 0 (no activity yet) so a tab that loads and is immediately
     * abandoned never produces a single ping — strict "no activity =
     * no row" semantics.
     */
    let lastActivityAt = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let initialTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    /**
     * Send the active-time delta accumulated since `lastPingAt`,
     * unless the user has been idle the whole interval. Skipping the
     * ping is the cheap path: no network call, the server doesn't
     * even know the visitor exists yet.
     *
     * When we skip, we still advance `lastPingAt` to "now" so a long
     * idle period doesn't all get attributed when the user comes
     * back — only the time since they re-engaged will count.
     */
    const ping = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      const wasActive = now - lastActivityAt < IDLE_THRESHOLD_MS;
      if (!wasActive) {
        // Idle — skip the network call but advance baseline so the
        // next ping (when they come back) only counts post-return time.
        lastPingAt = now;
        return;
      }
      const deltaMs = now - lastPingAt;
      lastPingAt = now;
      // Fire and forget. The server clamps to 60s; zero/negative
      // deltas are silently ignored on the backend.
      const { timezone, language } = readCoarseLocale();
      void api
        .recordVisitorActivity({
          visitorId,
          deltaMs,
          audience: getAudience(),
          timezone,
          language,
          landingVariant: getLandingVariant(),
          // Where they came from: only on the first ping of a page load.
          ...takeTouchesForHeartbeat(),
        })
        .catch(() => {
          // Silent — heartbeat misses are tolerable, never surface.
        });
    };

    /**
     * Mark "user did something". Called from every activity event.
     * Just stamps the timestamp; no React state, no re-renders.
     */
    const markActive = () => {
      lastActivityAt = Date.now();
    };

    /**
     * Tab became visible — count that as activity (the user came
     * back to look at us) and re-baseline so the hidden period
     * doesn't get attributed.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        lastPingAt = now;
        lastActivityAt = now;
      }
    };

    /**
     * Last-chance ping when the user is leaving the page. Same idle
     * check applies — if they tabbed away without ever interacting,
     * we don't credit time.
     */
    const handlePageHide = () => {
      ping();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    for (const eventName of ACTIVITY_EVENTS) {
      // `passive: true` on the activity listeners so we never delay
      // scroll/touch handling. We're only timestamping, not preventing.
      window.addEventListener(eventName, markActive, { passive: true });
    }

    // First ping shortly after load — the activity gate inside `ping`
    // decides whether anything actually goes over the wire.
    initialTimeoutId = setTimeout(() => {
      ping();
      intervalId = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    }, INITIAL_PING_DELAY_MS);

    return () => {
      cancelled = true;
      if (initialTimeoutId !== null) clearTimeout(initialTimeoutId);
      if (intervalId !== null) clearInterval(intervalId);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
      window.removeEventListener('pagehide', handlePageHide);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActive);
      }
    };
  }, []);
}
